/** Existing Company Profile interface keys, shared by appearance controls and action buttons. */
export type InterfaceTokens = {
  brandPrimary: string;
  brandPrimaryText: string;
  brandSecondary: string;
  link: string;
  actionFill: string;
  actionText: string;
  actionBorder: string;
  secondaryFill: string;
  secondaryBorder: string;
  disabledFill: string;
  disabledText: string;
};
export const defaultInterfaceTokens: InterfaceTokens = {
  brandPrimary: '#e8bf21', brandPrimaryText: '#1a1918', brandSecondary: '#1a1918', link: '#7d5e00',
  actionFill: '#202020', actionText: '#f7df9c', actionBorder: '#7c6330',
  secondaryFill: '#ffffff', secondaryBorder: '#1a1918', disabledFill: '#eeeeee', disabledText: '#626262'
};
export const INTERFACE_TOKEN_KEYS = Object.keys(defaultInterfaceTokens) as Array<keyof InterfaceTokens>;
export function resolveInterfaceTokens(configuration: unknown): InterfaceTokens {
  const source = configuration && typeof configuration === 'object'
    ? (configuration as { interfaceTokens?: Record<string, unknown> }).interfaceTokens : null;
  return Object.fromEntries(INTERFACE_TOKEN_KEYS.map(key => {
    const value = source?.[key];
    return [key, typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : defaultInterfaceTokens[key]];
  })) as InterfaceTokens;
}
const rgba = (hex: string, alpha: number) => `rgba(${[1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16)).join(',')},${alpha})`;
export function interfaceTokenVariables(tokens: InterfaceTokens): Record<string, string> {
  const customized = ['actionFill', 'actionText', 'actionBorder'].some(key => tokens[key] !== defaultInterfaceTokens[key]);
  return {
    '--button-action-fill': tokens.actionFill,
    '--button-action-text': tokens.actionText,
    '--button-action-border': tokens.actionBorder,
    '--button-action-gradient': customized
      ? `linear-gradient(180deg,${rgba(tokens.actionBorder, 0.22)},rgba(255,255,255,0.06))`
      : 'linear-gradient(180deg,rgba(245,206,123,0.22),rgba(255,255,255,0.06))',
    '--button-action-shadow': customized ? `0 10px 26px ${rgba(tokens.actionBorder, 0.12)}` : '0 10px 26px rgba(232,191,33,0.12)',
    '--button-action-hover-fill': customized
      ? '#' + [1, 3, 5].map(i => {
          const channel = parseInt(tokens.actionFill.slice(i, i + 2), 16);
          return Math.round(channel + (255 - channel) * 0.08).toString(16).padStart(2, '0');
        }).join('')
      : '#303030',
    '--button-secondary-fill': tokens.secondaryFill,
    '--button-secondary-text': tokens.brandSecondary,
    '--button-secondary-border': tokens.secondaryBorder,
    '--button-secondary-hover-fill': '#' + [1, 3, 5].map(i => {
      const channel = parseInt(tokens.secondaryFill.slice(i, i + 2), 16);
      return Math.round(channel > 127 ? channel * 0.92 : channel + (255 - channel) * 0.08)
        .toString(16).padStart(2, '0');
    }).join(''),
    '--button-disabled-fill': tokens.disabledFill,
    '--button-disabled-text': tokens.disabledText,
    '--button-disabled-border': tokens.disabledFill !== defaultInterfaceTokens.disabledFill ? tokens.disabledFill : '#bdbdbd'
  };
}
